// TicketDetailDrawer.jsx — panel detail tiket SDP (dibuka saat row Requests diklik).
// Data awal: raw_json dari snapshot DB (langsung tampil).
// Data live: GET /api/sdp/requests/:id/live → { request, notes, conversations, errors }.
// Attachment diunduh lewat GET /api/sdp/download (proxy backend).
// Isi HTML (description/notes/email) dirender di iframe sandbox TANPA allow-scripts.
import React, { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import {
  authGetJson, dv, who, listWho, isEmptyHtml, HtmlFrame, S, statusColor, Field, Attachments, DrawerShell,
} from "./sdpShared";

export default function TicketDetailDrawer({ row, onClose }) {
  const snap = (() => { try { return JSON.parse(row.raw_json || "{}"); } catch (e) { return {}; } })();
  const [live, setLive] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");
  const [tab, setTab] = useState("conversations");

  // force=true (tombol Reload) → lewati cache backend dan ambil langsung dari SDP Cloud.
  const load = async (force = false) => {
    setLoading(true); setLoadErr("");
    try { setLive(await authGetJson(`/api/sdp/requests/${encodeURIComponent(row.id)}/live${force === true ? "?refresh=1" : ""}`)); }
    catch (e) { setLoadErr(e.message); }
    setLoading(false);
  };
  useEffect(() => { setLive(null); load(); /* eslint-disable-next-line */ }, [row.id]);

  const q = (live && live.request) || snap || {};
  const status = dv(q.status) || row.status;
  const requester = q.requester || {};
  const notes = (live && live.notes) || [];
  const convs = (live && live.conversations) || [];
  const errs = (live && live.errors) || {};
  const resolution = q.resolution && (q.resolution.content || q.resolution);
  const ts = (v) => Number(v && typeof v === "object" ? v.value : 0) || 0;
  const convSorted = [...convs].sort((a, b) => ts(a.sent_time || a.created_time || a.time) - ts(b.sent_time || b.created_time || b.time));
  const notesSorted = [...notes].sort((a, b) => ts(a.created_time) - ts(b.created_time));
  const att = (list) => <Attachments list={list} module="requests" id={row.id} />;

  return (
    <DrawerShell
      label={`Ticket #${row.display_id}`}
      loading={loading}
      onReload={() => load(true)}
      onClose={onClose}
      title={q.subject || row.subject || "(no subject)"}
      top={<>
        <span style={{ fontWeight: 700, color: "rgb(var(--brand-700))" }}>#{row.display_id}</span>
        {status && <span style={{ ...S.badge, ...statusColor(status) }}>{status}</span>}
        {dv(q.priority) && <span style={{ ...S.badge, background: "#f1f5f9", color: "#334155" }}>{dv(q.priority)}</span>}
      </>}
    >
      {loadErr && <div style={S.err}><AlertTriangle size={14} /> Failed to load live data from SDP: {loadErr} — showing snapshot data.</div>}

      <div style={S.card}>
        <div style={S.h}>Details</div>
        <div style={S.grid}>
          <Field label="Requester" value={dv(requester.name) || row.requester_name} />
          <Field label="Email" value={requester.email_id || row.requester_email} />
          <Field label="Department" value={dv(q.department) || dv(requester.department) || row.requester_department} />
          <Field label="Job Title" value={requester.job_title || row.requester_job_title} />
          <Field label="Site" value={dv(q.site) || row.site} />
          <Field label="Group" value={dv(q.group) || row.group_name} />
          <Field label="Technician" value={dv(q.technician) || row.technician} />
          <Field label="Mode" value={dv(q.mode)} />
          <Field label="Template" value={dv(q.template) || row.template} />
          <Field label="Category" value={[dv(q.category), dv(q.subcategory), dv(q.item)].filter(Boolean).join(" › ")} />
          <Field label="SLA" value={dv(q.sla)} />
          <Field label="Created" value={dv(q.created_time) || row.created_time} />
          <Field label="Due By" value={dv(q.due_by_time) || row.due_by_time} />
          <Field label="Responded" value={dv(q.responded_time)} />
          <Field label="Resolved" value={dv(q.resolved_time)} />
          <Field label="Completed" value={dv(q.completed_time)} />
          <Field label="Overdue" value={q.is_overdue ? "Yes" : ""} />
          <Field label="Assets" value={Array.isArray(q.assets) ? q.assets.map((a) => a.name).filter(Boolean).join(", ") : ""} />
        </div>
      </div>

      <div style={S.card}>
        <div style={S.h}>Description</div>
        {isEmptyHtml(q.description) ? <div style={S.muted}>No description.</div> : <HtmlFrame html={q.description} />}
        {att(q.attachments)}
      </div>

      {!isEmptyHtml(typeof resolution === "string" ? resolution : "") && (
        <div style={S.card}>
          <div style={S.h}>Resolution</div>
          <HtmlFrame html={resolution} />
          {att(q.resolution && q.resolution.attachments)}
        </div>
      )}

      <div style={S.card}>
        <div style={{ display: "flex", gap: 4, borderBottom: "1px solid #e2e8f0", marginBottom: 6 }}>
          <button style={{ ...S.tab, ...(tab === "conversations" ? S.tabOn : {}) }} onClick={() => setTab("conversations")}>
            Conversations {live ? `(${convs.length})` : ""}
          </button>
          <button style={{ ...S.tab, ...(tab === "notes" ? S.tabOn : {}) }} onClick={() => setTab("notes")}>
            Notes {live ? `(${notes.length})` : ""}
          </button>
        </div>

        {loading && !live && <div style={S.muted}>Loading from ServiceDesk Plus…</div>}

        {tab === "conversations" && live && (
          <>
            {errs.conversations && <div style={S.err}><AlertTriangle size={14} /> {errs.conversations}</div>}
            {!errs.conversations && !convSorted.length && <div style={S.muted}>No conversations.</div>}
            {convSorted.map((c, i) => (
              <div key={c.id || i} style={i ? S.item : { paddingTop: 6 }}>
                <div style={S.meta}>
                  <b style={{ color: "#0f172a" }}>{who(c.from) || dv(c.created_by) || "—"}</b>
                  {listWho(c.to) && <span>to {listWho(c.to)}</span>}
                  <span>{dv(c.sent_time) || dv(c.created_time) || dv(c.time)}</span>
                  {c.type && <span style={{ ...S.badge, background: "#f1f5f9", color: "#475569" }}>{dv(c.type)}</span>}
                </div>
                {c.subject && <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>{c.subject}</div>}
                {c._error ? <div style={S.err}>{c._error}</div>
                  : isEmptyHtml(c.description) ? <div style={S.muted}>(no content)</div>
                  : <HtmlFrame html={c.description} />}
                {att(c.attachments)}
              </div>
            ))}
          </>
        )}

        {tab === "notes" && live && (
          <>
            {errs.notes && <div style={S.err}><AlertTriangle size={14} /> {errs.notes}</div>}
            {!errs.notes && !notesSorted.length && <div style={S.muted}>No notes.</div>}
            {notesSorted.map((n, i) => (
              <div key={n.id || i} style={i ? S.item : { paddingTop: 6 }}>
                <div style={S.meta}>
                  <b style={{ color: "#0f172a" }}>{dv(n.created_by) || "—"}</b>
                  <span>{dv(n.created_time)}</span>
                  {n.show_to_requester === false && <span style={{ ...S.badge, background: "#fef3c7", color: "#92400e" }}>Private</span>}
                </div>
                {n._error ? <div style={S.err}>{n._error}</div>
                  : isEmptyHtml(n.description) ? <div style={S.muted}>(no content)</div>
                  : <HtmlFrame html={n.description} />}
                {att(n.attachments)}
              </div>
            ))}
          </>
        )}
      </div>

      {live && live.fetched_at && <div style={{ ...S.muted, textAlign: "right" }}>
        {live.cached ? "Cached" : "Live from SDP"} · {new Date(live.fetched_at).toLocaleString()}
        {live.cached && " · tekan Reload untuk ambil terbaru"}
      </div>}
    </DrawerShell>
  );
}
